/**
 * /admin/tickets — every support ticket in the tenant (Administrator).
 *
 * A ticket is a question Explore could not answer that its asker escalated
 * (ADR-0031). The table shows number, status, the question (truncated), who
 * asked, when, and the collections it was asked against; a status filter
 * narrows it. A row opens the detail page, where the status changes. Names are
 * resolved by the backend at view time.
 */

import { useCallback, useEffect, useState } from "react";
import { Alert, Group, Loader, Paper, Select, Stack, Table, Text, Title } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  listTickets,
  TICKET_STATUSES,
  type TicketOut,
  type TicketStatus,
} from "@/shared/api/tickets";
import { askedBy, formatUtc, STATUS_LABEL } from "./ticketFormat";
import { TicketStatusBadge } from "./ticketStatus";

const QUESTION_CHARS = 90;

function truncate(text: string): string {
  return text.length > QUESTION_CHARS ? `${text.slice(0, QUESTION_CHARS - 1)}…` : text;
}

export function TicketsList() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [status, setStatus] = useState<TicketStatus | null>(null);
  const [items, setItems] = useState<TicketOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setItems(null);
    setError(null);
    try {
      const token = await getToken();
      setItems((await listTickets(token, status)).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setItems([]);
    }
  }, [getToken, status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Title order={2}>Tickets</Title>
        <Text c="dimmed" size="sm">
          Questions Explore could not answer from the selected documents, escalated by the people
          who asked them.
        </Text>
      </Stack>

      <Group>
        <Select
          label="Status"
          placeholder="All"
          value={status}
          onChange={(v) => setStatus((v as TicketStatus | null) ?? null)}
          data={TICKET_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
          clearable
          w={200}
          data-testid="ticket-status-filter"
        />
      </Group>

      {error && (
        <Alert variant="light" color="red" title="Could not load tickets">
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p={0}>
        {items === null ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : items.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">
            No tickets{status ? ` with status “${STATUS_LABEL[status]}”` : ""}.
          </Text>
        ) : (
          <Table verticalSpacing="sm" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ticket</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Question</Table.Th>
                <Table.Th>Asked by</Table.Th>
                <Table.Th>Raised (UTC)</Table.Th>
                <Table.Th>Collections</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((t) => (
                <Table.Tr
                  key={t.ticket_id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/admin/tickets/${t.ticket_id}`)}
                  data-testid="ticket-row"
                >
                  <Table.Td>
                    <Text size="sm" fw={500} ff="monospace">
                      {t.ticket_number}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <TicketStatusBadge status={t.status} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" title={t.question}>
                      {truncate(t.question)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{askedBy(t)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatUtc(t.created_at_utc)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {t.collections.map((c) => c.name ?? c.index_id).join(", ") || "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
